import { useId, useMemo, useState, type DragEvent, type KeyboardEvent } from 'react';
import { Link } from 'react-router';
import { failureOf, said } from '@/api/errors';
import { useCloseTask, useTaskBoard, useUpdateTask } from '@/api/hooks';
import type { WireTask } from '@/api/wire';
import { useMeOrThrow } from '@/app/MeContext';
import { ContextPanel, DetailsButton } from '@/ui/context';
import { Badge, Button, IconButton, Kicker, Segment, Skeleton, Switch } from '@/ui/primitives';
import { holderOf, NewTask } from './Assign';
import { RemoveDialog } from './Remove';
import { useFrameMaybe } from '@/app/frame';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { useWho, type Who } from '@/ui/who';
import { explainTask, gistOf } from './explain';

/**
 * The Tasks board (ADR-0149). Four columns a person works in; a card is
 * dragged between them to change what state it is in, and within one to
 * change its place. Drag is never the only way: every card has buttons that
 * do the same, so a keyboard, a screen reader and a phone all move a task.
 *
 * Done is the one column a card cannot leave: a task is closed through its
 * own press, and reopening one is making a new task.
 */

export type Column = 'open' | 'in_progress' | 'waiting' | 'done';

export const COLUMNS: ReadonlyArray<{ id: Column; label: string; blurb: string }> = [
  { id: 'open', label: 'To do', blurb: 'Not started' },
  { id: 'in_progress', label: 'Doing', blurb: 'Started' },
  { id: 'waiting', label: 'Waiting', blurb: 'On someone or something' },
  { id: 'done', label: 'Done', blurb: 'The last fourteen days' },
];

const STEP = 1024;

/** The rank each card effectively has: its own, or where it sits when it has none. */
export function effectiveRanks(tasks: readonly WireTask[]): number[] {
  const out: number[] = [];
  let last = 0;
  for (const t of tasks) {
    const rank = typeof t.boardRank === 'number' && t.boardRank > last ? t.boardRank : last + STEP;
    out.push(rank);
    last = rank;
  }
  return out;
}

/** The rank that puts a card at `index` in a column (its own card left out). */
export function rankAt(column: readonly WireTask[], index: number): number {
  const ranks = effectiveRanks(column);
  const before = index > 0 ? ranks[index - 1]! : 0;
  const after = index < ranks.length ? ranks[index]! : before + 2 * STEP;
  return (before + after) / 2;
}

export function columnOf(t: WireTask): Column {
  return t.state === 'cancelled' ? 'done' : t.state;
}

/** A card moves between columns only when it is a person's own work: not an agent's, a twin's or a procedure's. */
export function movable(t: WireTask): boolean {
  return Boolean(t.mayEdit) && !t.procedure && !t.assigneeAgent && t.state !== 'done' && t.state !== 'cancelled';
}

/** Whether a task may be removed here: the task page's rule, so a card offers exactly what its page does (ADR-0158). */
export function removable(t: WireTask): boolean {
  const closed = t.state === 'done' || t.state === 'cancelled';
  return Boolean(t.mayEdit) && !closed && !t.request && t.originKind !== 'request' && !(t.assigneeAgent && t.state === 'in_progress');
}

/** Whether a card offers "Give to my twin": the person's own open task, not an agent's or a procedure's (ADR-0164). */
export function givable(t: WireTask, meId: string): boolean {
  return t.assigneeHuman === meId && !t.procedure && !t.request && t.state !== 'done' && t.state !== 'cancelled';
}

export function TaskBoard() {
  const me = useMeOrThrow();
  const who = useWho();
  const toast = useToast();
  const [everyone, setEveryone] = useState(false);
  const q = useTaskBoard(me.department, everyone);
  const update = useUpdateTask();
  const close = useCloseTask();
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<{ column: Column; index: number } | null>(null);
  const [making, setMaking] = useState(false);
  // Mine, the ones I gave someone here (ADR-0157), or both.
  const [whose, setWhose] = useState<'all' | 'mine' | 'given'>('all');

  const columns = useMemo(() => {
    const by: Record<Column, WireTask[]> = { open: [], in_progress: [], waiting: [], done: [] };
    for (const t of q.data?.tasks ?? []) {
      if (whose === 'mine' && t.assigneeHuman !== me.humanId) continue;
      if (whose === 'given' && !t.delegated) continue;
      by[columnOf(t)].push(t);
    }
    return by;
  }, [q.data, whose, me.humanId]);

  const failed = (error: unknown) => toast({ text: said(failureOf(error)), tone: 'danger' });

  /** Move a card to `index` in `column`: one request for the place, and the close when it lands in Done. */
  const move = (task: WireTask, column: Column, index: number) => {
    const target = columns[column].filter((t) => t.id !== task.id);
    if (column === 'done') {
      close.mutate({ taskId: task.id, state: 'done' }, { onSuccess: () => toast({ text: `“${task.title}” is done.` }), onError: failed });
      return;
    }
    const boardRank = rankAt(target, Math.max(0, Math.min(index, target.length)));
    update.mutate(
      { taskId: task.id, boardRank, ...(column !== task.state ? { state: column } : {}) },
      { onError: failed },
    );
  };

  const find = (id: string | null) => (id ? (q.data?.tasks ?? []).find((t) => t.id === id) ?? null : null);

  const onDrop = (column: Column, index: number) => (e: DragEvent) => {
    e.preventDefault();
    const task = find(e.dataTransfer.getData('text/plain') || dragging);
    setDragging(null);
    setOver(null);
    if (!task || !movable(task)) return;
    move(task, column, index);
  };

  if (q.isPending) return <Skeleton rows={6} />;
  if (q.isError) return <Refusal failure={failureOf(q.error)} />;

  return (
    <div className="flex flex-col gap-4" data-testid="task-board">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="m-0 max-w-[62ch] text-dense text-secondary">
          Drag a card to another column to move it on, or up and down to put it in order. Each card’s buttons do the same without a mouse.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {q.data.lead && <Switch checked={everyone} onChange={setEveryone} label="Everyone’s in this department" />}
          <Button variant="primary" onClick={() => setMaking(true)}>
            New task
          </Button>
        </div>
      </div>
      <Segment
        label="Whose tasks"
        value={whose}
        options={[
          { value: 'all', label: 'All' },
          { value: 'mine', label: 'Mine' },
          { value: 'given', label: 'Given to others' },
        ]}
        onChoose={setWhose}
      />
      {making && <NewTask onClose={() => setMaking(false)} />}
      {/* Columns by the room the board has, not the window: the chat beside it takes a third. */}
      <div className="@container">
      <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-2 @5xl:grid-cols-4">
        {COLUMNS.map((c) => {
          const cards = columns[c.id];
          return (
            <section
              key={c.id}
              aria-label={c.label}
              data-testid={`column-${c.id}`}
              onDragOver={(e) => {
                if (!dragging) return;
                e.preventDefault();
                setOver((o) => (o?.column === c.id ? o : { column: c.id, index: cards.length }));
              }}
              onDrop={onDrop(c.id, over?.column === c.id ? over.index : cards.length)}
              className={`flex min-h-[160px] flex-col gap-2 border bg-sunken p-3 ${over?.column === c.id ? 'border-accent' : 'border-line'}`}
            >
              <header className="flex items-baseline justify-between gap-2">
                <Kicker>{c.label}</Kicker>
                <span className="text-micro text-meta">{cards.length}</span>
              </header>
              <p className="m-0 text-micro text-meta">{c.blurb}</p>
              {cards.map((t, i) => (
                <Card
                  key={t.id}
                  task={t}
                  column={c.id}
                  index={i}
                  count={cards.length}
                  who={who}
                  meId={me.humanId}
                  onDone={() => move(t, 'done', 0)}
                  doneBusy={close.isPending}
                  dragging={dragging === t.id}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', t.id);
                    e.dataTransfer.effectAllowed = 'move';
                    setDragging(t.id);
                  }}
                  onDragEnd={() => {
                    setDragging(null);
                    setOver(null);
                  }}
                  onDragOverCard={() => setOver({ column: c.id, index: i })}
                  onMove={(column, index) => move(t, column, index)}
                  holder={holderOf(t, q.data.assignees, me.humanId)}
                />
              ))}
              {cards.length === 0 && <p className="m-0 py-4 text-center text-dense text-meta">Nothing here.</p>}
            </section>
          );
        })}
      </div>
      </div>
    </div>
  );
}

function Card({
  task,
  column,
  index,
  count,
  who,
  meId,
  onDone,
  doneBusy,
  dragging,
  onDragStart,
  onDragEnd,
  onDragOverCard,
  onMove,
  holder,
}: {
  task: WireTask;
  column: Column;
  index: number;
  count: number;
  who: Who;
  meId: string;
  onDone: () => void;
  doneBusy: boolean;
  dragging: boolean;
  onDragStart: (e: DragEvent) => void;
  onDragEnd: () => void;
  onDragOverCard: () => void;
  onMove: (column: Column, index: number) => void;
  /** Who has it, when it is not the viewer: a colleague or an agent (ADR-0157). */
  holder?: string | null;
}) {
  const can = movable(task);
  const frame = useFrameMaybe();
  const [removing, setRemoving] = useState(false);
  const mayRemove = removable(task);
  const mayGive = Boolean(frame?.twin) && givable(task, meId);
  const at = COLUMNS.findIndex((c) => c.id === column);
  const left = at > 0 ? COLUMNS[at - 1]!.id : null;
  const right = at < COLUMNS.length - 1 ? COLUMNS[at + 1]!.id : null;
  // Alt and an arrow moves the focused card, as its buttons do.
  const onKey = (e: KeyboardEvent) => {
    if (!can || !e.altKey) return;
    if (e.key === 'ArrowLeft' && left) onMove(left, 0);
    else if (e.key === 'ArrowRight' && right) onMove(right, 0);
    else if (e.key === 'ArrowUp' && index > 0) onMove(column, index - 1);
    else if (e.key === 'ArrowDown' && index < count - 1) onMove(column, index + 2);
    else return;
    e.preventDefault();
  };
  // Every card opens as an accordion: what it is, where it came from, who
  // owns it and what to do (ADR-0155). The link to its page stays.
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const due = task.dueAt ? new Date(task.dueAt) : null;
  const late = due !== null && due.getTime() < Date.now() && task.state !== 'done';
  const context = explainTask(task, who, new Date(), {
    meId,
    ...(can ? { markDone: onDone, busy: doneBusy } : {}),
  });
  return (
    <article
      draggable={can}
      onDragStart={can ? onDragStart : undefined}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOverCard();
      }}
      onKeyDown={onKey}
      tabIndex={0}
      aria-label={task.title}
      data-testid="task-card"
      className={`flex flex-col gap-2 border border-line bg-raised p-3 ${can ? 'cursor-grab' : ''} ${dragging ? 'opacity-50' : ''}`}
    >
      <div className="flex items-start justify-between gap-1">
        <Link to={`/tasks/${task.id}`} className="inline-flex min-h-tap items-center text-dense font-medium text-ink no-underline hover:underline">
          {task.title}
        </Link>
        <DetailsButton open={open} onToggle={() => setOpen((o) => !o)} controls={panelId} label={task.title} />
      </div>
      {/* What it wants, in one line, before anyone opens it (ADR-0155). */}
      <p className="m-0 text-dense text-secondary" data-testid="task-gist" title={context.need ?? context.next.tip}>
        {gistOf(context)}
      </p>
      <div className="flex flex-wrap gap-1">
        {task.priority <= 2 && <Badge plain tone="warning">First</Badge>}
        {due && <Badge plain tone={late ? 'danger' : 'neutral'}>{late ? 'Late · ' : 'Due '}{due.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</Badge>}
        {task.hold && <Badge plain tone="accent">In calendar {new Date(task.hold.startsAt).toLocaleString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}</Badge>}
        {task.request && <Badge plain>Asked by another department</Badge>}
        {holder && <Badge plain tone={task.assigneeAgent ? 'accent' : 'neutral'}>With {holder}</Badge>}
        {(task.waitingOn?.length ?? 0) > 0 && <Badge plain tone="warning">Waits on {task.waitingOn!.length === 1 ? `“${task.waitingOn![0]!.title}”` : `${task.waitingOn!.length} tasks`}</Badge>}
      </div>
      {(mayRemove || mayGive) && (
        <div className="flex flex-wrap items-center gap-1" aria-label="Hand on or remove">
          {mayGive && (
            <Button size="row" onClick={() => frame!.ask(`Look into this task: “${task.title.slice(0, 300)}”. Tell me what you find, with sources.`, { page: 'task', taskId: task.id })}>
              Give to {frame!.twin}
            </Button>
          )}
          {mayRemove && <IconButton label={`Remove “${task.title}”`} glyph="trash" onClick={() => setRemoving(true)} />}
        </div>
      )}
      {removing && <RemoveDialog target={{ taskId: task.id }} title={task.title} onClose={() => setRemoving(false)} />}
      {can && (
        <div className="flex flex-wrap gap-1" aria-label="Move">
          {left && (
            <IconButton label={`Move to ${COLUMNS[at - 1]!.label}`} glyph="back" onClick={() => onMove(left, 0)} />
          )}
          {index > 0 && (
            <IconButton label="Move up" glyph="up" onClick={() => onMove(column, index - 1)} />
          )}
          {index < count - 1 && (
            <IconButton label="Move down" glyph="down" onClick={() => onMove(column, index + 2)} />
          )}
          {right && (
            <IconButton label={`Move to ${COLUMNS[at + 1]!.label}`} glyph="forward" onClick={() => onMove(right, 0)} />
          )}
        </div>
      )}
      {open && (
        // What was opened is read, not dragged: a drag that starts in the
        // panel must not carry the card, and its keys are not the card's.
        <div
          draggable={false}
          onDragStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <ContextPanel id={panelId} context={context} />
        </div>
      )}
    </article>
  );
}
